import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { CircleAlert, X } from 'lucide-react'
import { Tooltip } from './tooltip'

export const inputVariants = cva(
  'peer flex w-full rounded-full border font-normal z-[1] border-neutral-300 bg-background text-base file:border-0 file:bg-transparent file:font-medium file:text-foreground placeholder:text-neutral-400 text-neutral-800 focus:border-neutral-500 enabled:hover:border-neutral-500 transition focus-visible:outline-none disabled:cursor-not-allowed disabled:border-neutral-200 disabled:text-neutral-300 disabled:bg-neutral-100  placeholder:font-normal  placeholder:text-base  placeholder:font-sans max-md:placeholder:text-sm',
  {
    variants: {
      size: {
        xs: 'h-8 py-2 px-4',
        sm: 'h-9 px-4 text-sm py-2.5',
        md: 'h-[45px] py-3 px-6',
        lg: 'h-13.5 py-4.5 px-6 font-medium placeholder:font-medium placeholder:text-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

const labelFloatingVariants = cva(
  'peer-focus:secondary peer-focus:text-neutral-500 text-neutral-500 absolute top-1 !mt-0 origin-[0] -translate-y-4 scale-[0.8] transform cursor-text bg-background duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-[4px] peer-focus:-translate-y-4 peer-focus:scale-[0.8] px-2 peer-focus:px-2 rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4 font-normal text-base font-sans peer-placeholder-shown:text-neutral-300 peer-placeholder-shown:px-0 max-md:text-sm line-clamp-1 pr-2 ',
  {
    variants: {
      size: {
        xs: 'start-4',
        sm: 'start-4',
        md: 'start-6',
        lg: 'start-6 font-medium text-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

interface InputProps
  extends Omit<React.ComponentProps<'input'>, 'size' | 'prefix' | 'suffix'>,
    VariantProps<typeof inputVariants> {
  label?: React.ReactNode
  helpText?: string
  tooltip?: string
  error?: { message?: string } | boolean
  floating?: boolean
  wrapperClassName?: string
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  isRequired?: boolean
}

const ErrorIcon = ({ error }: { error?: boolean }) => (
  <CircleAlert
    className={cn(
      'mr-1 h-3.5 w-3.5 fill-neutral-400 stroke-neutral-400 [&_line]:stroke-light',
      error && 'fill-error-500 stroke-error-500 [&_line]:stroke-light'
    )}
  />
)

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClassName,
      className,
      type,
      size,
      label,
      error,
      helpText,
      tooltip,
      floating,
      id,
      prefix,
      suffix,
      placeholder = '',
      isRequired,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId()
    const inputId = id || generatedId
    const errorMessage = typeof error === 'object' ? error?.message : undefined

    return (
      <div className={cn('relative space-y-1', wrapperClassName)}>
        {label && !floating && (
          <span className='flex items-center space-x-1'>
            <label
              htmlFor={inputId}
              className={cn(
                'ml-3 font-sans text-sm leading-4 text-neutral-600'
              )}
            >
              {label}
              {isRequired && <span className='ml-0.5 text-error-500'>*</span>}
            </label>
            {tooltip && (
              <Tooltip content={tooltip}>
                <CircleAlert className='h-3.5 w-3.5 cursor-help stroke-neutral-500' />
              </Tooltip>
            )}
          </span>
        )}
        <div>
          <div className={cn('relative')}>
            {prefix && (
              <span className='absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-neutral-800'>
                {prefix}
              </span>
            )}
            <input
              id={inputId}
              type={type}
              value={props.value}
              onChange={(e) => {
                props.onChange?.(e)
              }}
              ref={ref}
              className={cn(
                inputVariants({ size }),
                error &&
                  errorMessage &&
                  'border-error-500 hover:border-error-500 focus:border-error-500',
                prefix && 'pl-10',
                (suffix || (type === 'search' && props.value)) && 'pr-10',
                className
              )}
              aria-invalid={!!error}
              placeholder={placeholder}
              {...props}
            />

            {type === 'search' && props.value ? (
              <button
                type='button'
                className='absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-neutral-800'
                onClick={() => {
                  props.onChange?.({
                    target: { value: '' },
                  } as React.ChangeEvent<HTMLInputElement>)
                }}
              >
                <X size={16} />
              </button>
            ) : null}
            {floating && label && (
              <label
                className={cn(
                  labelFloatingVariants({ size }),
                  prefix && 'ml-4'
                )}
                htmlFor={inputId}
              >
                {label}
              </label>
            )}
            {suffix && !(type === 'search' && props.value) && (
              <span className='absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-neutral-800'>
                {suffix}
              </span>
            )}
          </div>

          {(helpText || errorMessage) && (
            <span className='ml-2 mt-1 flex items-center'>
              <ErrorIcon error={!!errorMessage} />
              <p
                className={cn(
                  'whitespace-nowrap font-sans text-xs leading-4 text-neutral-400',
                  errorMessage && 'text-error-500'
                )}
              >
                {errorMessage || helpText}
              </p>
            </span>
          )}
        </div>
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
